import type { LineEdit, LineEdits, MaterialLineItemOption } from "../types";
import type { MaterialRowItem } from "./materialLineItems";

type ApplyFurnaceVentDefaultsParams = {
    attributes: Record<string, unknown>;
    materialsByGroup: Record<string, MaterialRowItem[]>;
    materialsEdits: LineEdits;
    onlyIfEmpty?: boolean;
};

type FurnaceVentEntry = { size: string; count: number };

function parseFurnaceVents(raw: unknown): FurnaceVentEntry[] {
    let entries = raw;

    if (typeof raw === "string" && raw.trim().startsWith("[")) {
        try {
            entries = JSON.parse(raw);
        } catch {
            return [];
        }
    }

    if (!Array.isArray(entries)) return [];

    return entries
        .map((entry: any) => ({
            size: String(entry?.Size ?? entry?.size ?? "").trim(),
            count: Number(entry?.Qty ?? entry?.qty ?? entry?.count) || 0,
        }))
        .filter((entry) => entry.size && entry.count > 0);
}

function normalizeSizeKey(size: string): string {
    return size.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Prefer generic sized SKU (e.g. mpd-gen-furnace-vent-4in), else first option. */
function pickFurnaceProduct(
    options: MaterialLineItemOption[],
    size: string,
): string | null {
    if (!options.length) return null;

    const sizeKey = normalizeSizeKey(size);
    const genericMatch = options.find((option) => {
        const id = option.ProductId.toLowerCase();
        const sub = normalizeSizeKey(option.Subcategory ?? "");
        const name = normalizeSizeKey(option.ProductName ?? "");
        return (
            id.includes("mpd-gen-furnace") &&
            (id.includes(sizeKey.replace("inch", "in")) ||
                sub.includes(sizeKey) ||
                name.includes(sizeKey))
        );
    });

    return genericMatch?.ProductId ?? options[0]?.ProductId ?? null;
}

/**
 * Auto-select generic furnace Jack/Cap/Collar products per inspection size,
 * similar to GutterSize → mpd-generic-gutter-5in attributeDefaults.
 */
export function applyFurnaceVentDefaults({
    attributes,
    materialsByGroup,
    materialsEdits,
    onlyIfEmpty = true,
}: ApplyFurnaceVentDefaultsParams): Record<string, Partial<LineEdit>> {
    const entries = parseFurnaceVents(attributes?.FurnaceVents);
    if (!entries.length) return {};

    const qtyBySize = new Map(
        entries.map((entry) => [entry.size, entry.count]),
    );
    const patches: Record<string, Partial<LineEdit>> = {};

    for (const rows of Object.values(materialsByGroup || {})) {
        for (const row of rows) {
            if (row.mode !== "multi" || row.derivedFrom !== "Furnace Vent") {
                continue;
            }

            for (const line of row.lines) {
                if (!line.size) continue;
                const qty = qtyBySize.get(line.size);
                if (!qty) continue;

                const existing = materialsEdits[line.lineId];
                if (onlyIfEmpty && existing?.selectedProductId) continue;

                const productId = pickFurnaceProduct(
                    line.availableProducts,
                    line.size,
                );
                if (!productId) continue;

                patches[line.lineId] = {
                    selectedProductId: productId,
                };
            }
        }
    }

    return patches;
}
